import { useTenant } from '../contexts/TenantContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { DEFAULT_ENABLED_MODULES } from '../constants';
import toast from 'react-hot-toast';

export const getModulesArray = (modules: any, fallbackToDefaults = true): string[] => {
  if (Array.isArray(modules)) {
    return modules;
  }
  if (modules && typeof modules === 'object') {
    return Object.entries(modules)
      .filter(([_, isEnabled]) => Boolean(isEnabled))
      .map(([key]) => key);
  }
  // Only apply default modules fallback when modules is strictly uninitialized (null/undefined)
  if (fallbackToDefaults && (modules === undefined || modules === null)) {
    return [...DEFAULT_ENABLED_MODULES];
  }
  return [];
};

export const useEnabledModules = (): string[] => {
  const { node } = useTenant();
  const baseModules = getModulesArray(node?.modules, true);
  const powerupModules = getModulesArray((node?.config as any)?.powerups, false);
  return Array.from(new Set([...baseModules, ...powerupModules]));
};

export const useModule = (moduleName: string): boolean => {
  const { node } = useTenant();
  const enabledModules = useEnabledModules();
  // PULSE is always on as per UI Bible
  if (moduleName === 'PULSE') return true;
  // SUPPORT is globally available
  if (moduleName === 'SUPPORT' && !node) return true;
  return enabledModules.includes(moduleName);
};

export const useTogglePowerUp = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ powerUp, enable }: { powerUp: string, enable: boolean }) => {
      // It toggles via either POST (enable) or DELETE (disable)
      const url = `/api/nodes/${nodeId}/powerups`;
      if (enable) {
        return apiWorker.post(url, { powerup: powerUp }).then(r => r.data);
      } else {
        return apiWorker.delete(`${url}?powerup=${powerUp}`).then(r => r.data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-config', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      queryClient.invalidateQueries({ queryKey: ['user-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      toast.success('Power-up updated successfully');
    },
    onError: () => {
      toast.error('Failed to update power-up status');
    }
  });
};
